<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { fade, scale } from 'svelte/transition';
  import { writable } from 'svelte/store';
  import { userRole, isReadOnly } from '$stores/sessionStore';

  const dispatch = createEventDispatcher();

  export let language = null;
  export let title = "Language";
  export let onClose = () => {};
  export let onSave = () => {};

  let isLoading = false;
  let errorMessage = '';
  let formData = writable({});
  let formLanguage = {};
  let isFetchingLanguageDetails = false;

  const unsubscribe = formData.subscribe(value => {
    formLanguage = value;
  });

  async function fetchLanguageDetailsFromOpenAI(code) {
    if (!code || code.length < 2) {
      errorMessage = 'Please enter a valid language code (e.g., en_US)';
      return;
    }
    
    try {
      isFetchingLanguageDetails = true;
      errorMessage = '';
      const response = await fetch('/api/v1/openai/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          messages: [
            {
              role: "system",
              content: `You are a helpful assistant that provides language details in JSON format. Given a language code (e.g., 'en_US'), return a JSON object with language details matching this exact format. Here are examples of the expected format:

Example 1:
{
    "language": "Danish",
    "country": "Denmark",
    "flag": "🇩🇰",
    "displayBase": "🇩🇰 Danish",
    "displayLocalized": "🇩🇰 Dansk",
    "rightToLeft": false,
    "region": "Denmark",
    "code": "da_DK"
}

Example 2:
{
    "language": "German",
    "country": "Germany",
    "flag": "🇩🇪",
    "displayBase": "🇩🇪 German",
    "displayLocalized": "🇩🇪 Deutsch",
    "rightToLeft": false,
    "region": "Germany",
    "code": "de_DE"
}

Important rules:
1. Always use the correct country flag emoji
2. displayBase should be in English: "flag language"
3. displayLocalized should be in the native language: "flag native_name"
4. rightToLeft should be true only for languages like Arabic, Hebrew, etc.
5. region should match the country name
6. code should match the input code exactly

Only return the JSON object, no other text.`
            },
            {
              role: "user",
              content: `Please provide language details for the code: ${code}`
            }
          ]
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch language details');
      }

      const data = await response.json();
      const languageDetails = JSON.parse(data.choices[0].message.content);

      // Update form with the fetched details
      formData.update(data => ({
        ...data,
        ...languageDetails
      }));

    } catch (error) {
      console.error('Error fetching language details:', error);
      errorMessage = 'Failed to generate language details. Please try again.';
    } finally {
      isFetchingLanguageDetails = false;
    }
  }

  onMount(() => {
    formData.set({
      ...language,
      language: language?.language || '',
      country: language?.country || '',
      flag: language?.flag || '',
      displayBase: language?.displayBase || '',
      displayLocalized: language?.displayLocalized || '',
      rightToLeft: language?.rightToLeft || false,
      region: language?.region || '',
      code: language?.code || ''
    });

    return () => {
      unsubscribe();
    };
  });

  function handleClose() {
    onClose();
  }

  function validateForm() {
    if (!formLanguage.language || !formLanguage.country || !formLanguage.code) {
      errorMessage = 'Language, country, and code are required fields';
      return false;
    }

    // Set default values if not provided
    if (!formLanguage.flag) {
      formData.update(data => ({ ...data, flag: '' }));
    }

    if (!formLanguage.displayBase) {
      formData.update(data => ({ 
        ...data, 
        displayBase: `${formLanguage.flag || ''} ${formLanguage.language}` 
      }));
    }

    if (!formLanguage.displayLocalized) {
      formData.update(data => ({ 
        ...data, 
        displayLocalized: `${formLanguage.flag || ''} ${formLanguage.language}` 
      }));
    }

    if (!formLanguage.region) {
      formData.update(data => ({ ...data, region: formLanguage.country }));
    }

    errorMessage = '';
    return true;
  }

  async function handleSave() {
    try {
      if (!validateForm()) {
        return;
      }

      isLoading = true;
      await onSave(formLanguage);
      handleClose();
    } catch (error) {
      errorMessage = error.message || 'Failed to save language';
      console.error('Error saving language:', error);
    } finally {
      isLoading = false;
    }
  }
</script>

<div 
  class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm"
  transition:fade={{ duration: 200 }}
>
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75" on:click={handleClose}></div>
  
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div 
    class="bg-white dark:bg-gray-900 p-4 rounded-lg shadow-lg w-full max-w-2xl relative z-60 overflow-y-auto max-h-[90vh]"
    on:click|stopPropagation
    transition:scale={{ duration: 300, start: 0.95 }}
  >
    <header class="flex justify-between items-start mb-4 text-gray-900 dark:text-gray-100">
      <h2 class="text-xl font-bold">{title}</h2>
      <button 
        class="text-2xl text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200" 
        on:click={handleClose}
      >&times;</button>
    </header>

    {#if errorMessage}
      <div class="mb-4 p-2 bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200 rounded">
        {errorMessage}
      </div>
    {/if}

    <div class="flex flex-col gap-4 mb-4">
      <!-- Row 1: Language, Country, Flag -->
      <div class="flex gap-3">
        <div class="w-2/5">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="language">
            Language*
          </label>
          <input
            type="text"
            id="language"
            bind:value={$formData.language}
            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            required
          />
        </div>
        
        <div class="w-2/5">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="country">
            Country*
          </label>
          <input
            type="text"
            id="country"
            bind:value={$formData.country}
            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            required
          />
        </div>
        
        <div class="w-1/5">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="flag">
            Flag
          </label>
          <input
            type="text"
            id="flag"
            bind:value={$formData.flag}
            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
          />
        </div>
      </div>

      <!-- Row 2: Code, Region, Right to Left -->
      <div class="flex gap-3">
        <div class="w-2/5">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="code">
            Code*
          </label>
          <div class="flex gap-2">
            <div class="relative flex-grow">
              <input
                type="text"
                id="code"
                bind:value={$formData.code}
                class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                required
              />
            </div>
            <button
              type="button"
              on:click={() => fetchLanguageDetailsFromOpenAI($formData.code)}
              class="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isFetchingLanguageDetails || !$formData.code}
            >
              {#if isFetchingLanguageDetails}
                <div class="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
              {:else}
                Generate
              {/if}
            </button>
          </div>
        </div>
        
        <div class="w-2/5">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="region">
            Region
          </label>
          <input
            type="text"
            id="region"
            bind:value={$formData.region}
            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
          />
        </div>
        
        <div class="w-1/5">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="rightToLeft">
            Right to Left
          </label>
          <select
            id="rightToLeft"
            bind:value={$formData.rightToLeft}
            class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
          >
            <option value={false}>No</option>
            <option value={true}>Yes</option>
          </select>
        </div>
      </div>

      <!-- Row 3: Display Base, Display Localized -->
      <div class="flex gap-3">
        <div class="w-1/2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="displayBase">
            Display Base
          </label>
          <input
            type="text"
            id="displayBase"
            bind:value={$formData.displayBase}
            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
          />
        </div>
        
        <div class="w-1/2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="displayLocalized">
            Display Localized
          </label>
          <input
            type="text"
            id="displayLocalized"
            bind:value={$formData.displayLocalized}
            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
          />
        </div>
      </div>
    </div>

    <div class="flex justify-end space-x-2 pt-2 border-t border-gray-200 dark:border-gray-700">
      <button 
        on:click={handleClose} 
        class="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-700 disabled:opacity-50"
        disabled={isLoading}
      >
        Cancel
      </button>
      <button 
        on:click={handleSave} 
        class="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-700 disabled:opacity-50"
        disabled={isReadOnly() || isLoading}
      >
        {isLoading ? 'Saving...' : 'Save'}
      </button>
    </div>
  </div>
</div> 